import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Command, Sparkles, TrendingUp, ShieldAlert, Users } from "lucide-react";

export const MigrationSituationRoom = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["mig-situation"],
    queryFn: async () => (await api.get("/migration/situation")).data,
    refetchInterval: 60000,
  });

  const d = data?.dashboard;
  const q = data?.quality;
  const f = data?.forecast;

  return (
    <div className="space-y-6" data-testid="page-migration-situation">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Executive Migration Command Centre</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <Command className="text-[#C99A2E]" /> National Migration Situation Room
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-4xl leading-relaxed">
          Real-time executive view of Zimbabwe's national cadastre digitisation programme. Refreshes automatically every 60 seconds.
        </div>
      </header>

      <div className="cad-panel p-5">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3">Executive headlines</div>
        <ol className="space-y-2">
          {(data?.headlines || []).map((h, i) => (
            <li key={i} data-testid={`sr-headline-${i}`} className="flex items-start gap-2 text-[14px] text-[#F6F8FA] leading-relaxed">
              <Sparkles size={14} className="text-[#C99A2E] mt-1" />{h}
            </li>
          ))}
          {isLoading && <li className="text-[#B3C2CC] italic text-[13px]">Loading executive intelligence…</li>}
        </ol>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Completion", value: `${d?.completion_pct ?? 0}%` },
          { label: "Published", value: (d?.published ?? 0).toLocaleString() },
          { label: "In Pipeline", value: (d?.in_pipeline ?? 0).toLocaleString() },
          { label: "Projected ETA", value: f?.estimated_completion ?? "—" },
        ].map((k) => (
          <div key={k.label} className="cad-panel p-4" data-testid={`sr-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{k.label}</div>
            <div className="cad-kpi-value text-3xl mt-1 text-[#F6F8FA]">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3 flex items-center gap-1.5">
            <TrendingUp size={12} /> Provincial performance
          </div>
          <table className="w-full text-[12.5px]">
            <thead><tr className="text-left border-b cad-hairline">
              {["Province", "Records", "Published", "Rate"].map((h) => (
                <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
              ))}
            </tr></thead>
            <tbody className="cad-tbody">
              {(d?.province_ranking || []).map((r) => (
                <tr key={r.province}>
                  <td className="px-2 py-1.5 text-[#F6F8FA]">{r.province}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#B3C2CC]">{r.total.toLocaleString()}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#B3C2CC]">{r.published.toLocaleString()}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[#6BB88A]">{r.rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3 flex items-center gap-1.5">
            <ShieldAlert size={12} /> Data quality watch
          </div>
          <div className="space-y-3">
            {(q?.issue_counts || []).slice(0, 6).map((i) => (
              <div key={i.issue}>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-[#F6F8FA]">{i.label}</span>
                  <span className="font-mono-cad text-[#C99A2E]">{i.count.toLocaleString()}</span>
                </div>
                <div className="h-1.5 bg-[#18232D] rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-[#C99A2E]" style={{ width: `${Math.min(100, (i.count / ((q?.issue_counts?.[0]?.count) || 1)) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="cad-panel p-5">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E] mb-3 flex items-center gap-1.5">
          <Users size={12} /> Top officer contributions this cycle
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {(data?.productivity?.officers || []).slice(0, 8).map((o) => (
            <div key={o.officer} className="border cad-hairline rounded-sm px-3 py-2 bg-[#071B2B]">
              <div className="text-[13px] text-[#F6F8FA] font-display font-bold truncate">{o.officer}</div>
              <div className="text-[10px] font-mono-cad text-[#C99A2E] mt-1">{o.advances} advances · {o.published} published</div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-[10px] font-mono-cad uppercase tracking-widest text-[#5F7688]">Demonstration data — refresh {data?.generated_at?.slice(0, 19)}</div>
    </div>
  );
};
